import React, { useEffect } from 'react';
import { Toaster } from 'sonner';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './api/query-client';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { SidebarProvider } from './context/SidebarContext';
import { AuthProvider } from './context/AuthContext';
import MainLayout from './components/Layout/MainLayout';
import SettingsLayout from './components/Layout/SettingsLayout';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import { PublicLayout } from './pages/Public/PublicLayout';
import { initDb } from './data/db';
import SignInPage from './pages/Auth/SignInPage';
import SignUpPage from './pages/Auth/SignUpPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import ChangePasswordPage from './pages/Auth/ChangePasswordPage';
import HomePage from './pages/Public/HomePage';
import TrialRegisterPage from './pages/Public/TrialRegisterPage';
import TipsPage from './pages/Public/TipsPage';
import DashboardPage from './pages/Dashboard/DashboardPage';
import HubPage from './pages/Dashboard/HubPage';
import BillingPage from './pages/Billing/BillingPage';
import BudgetPage from './pages/Budget/BudgetPage';
import BudgetCreatePage from './pages/Budget/BudgetCreatePage';
import BudgetDetailPage from './pages/Budget/BudgetDetailPage';
import CalendarPage from './pages/Calendar/CalendarPage';
import ReportsPage from './pages/Reports/ReportsPage';
import SettingsPage from './pages/Settings/SettingsPage';
import NotFoundPage from './pages/System/NotFoundPage';
import ClientsListPage from './pages/Clients/ClientsListPage';
import ClientCreatePage from './pages/Clients/ClientCreatePage';
import ClientDetailPage from './pages/Clients/ClientDetailPage';
import ClientEditPage from './pages/Clients/ClientEditPage';
import DocumentsListPage from './pages/Documents/DocumentsListPage';
import DocumentCreatePage from './pages/Documents/DocumentCreatePage';
import DocumentDetailPage from './pages/Documents/DocumentDetailPage';
import DocumentEditPage from './pages/Documents/DocumentEditPage';
import DocumentSigningPage from './pages/Documents/DocumentSigningPage';
import TasksListPage from './pages/Tasks/TasksListPage';
import TaskCreatePage from './pages/Tasks/TaskCreatePage';
import TaskDetailPage from './pages/Tasks/TaskDetailPage';
import TaskEditPage from './pages/Tasks/TaskEditPage';
import WorkflowsListPage from './pages/Workflows/WorkflowsListPage';
import WorkflowCreatePage from './pages/Workflows/WorkflowCreatePage';
import WorkflowDetailPage from './pages/Workflows/WorkflowDetailPage';
import WorkflowEditPage from './pages/Workflows/WorkflowEditPage';
import WorkflowDiagramPage from './pages/Workflows/WorkflowDiagramPage';
import TaskCreateForWorkflowPage from './pages/Tasks/TaskCreateForWorkflowPage';
import ProfilePage from './pages/Profile/ProfilePage';
import Chatbot from './components/Chatbot';
import CountriesListPage from './pages/Reference/CountriesListPage';
import CountryDetailPage from './pages/Reference/CountryDetailPage';
import StatesListPage from './pages/Reference/StatesListPage';
import StateDetailPage from './pages/Reference/StateDetailPage';
import CitiesListPage from './pages/Reference/CitiesListPage';
import CityDetailPage from './pages/Reference/CityDetailPage';
import CaseTypesListPage from './pages/Setup/CaseTypesListPage';
import CaseTypeCreatePage from './pages/Setup/CaseTypeCreatePage';
import CaseTypeDetailPage from './pages/Setup/CaseTypeDetailPage';
import CaseTypeEditPage from './pages/Setup/CaseTypeEditPage';
import CourtTypesListPage from './pages/Setup/CourtTypesListPage';
import CourtTypeCreatePage from './pages/Setup/CourtTypeCreatePage';
import CourtTypeDetailPage from './pages/Setup/CourtTypeDetailPage';
import CourtTypeEditPage from './pages/Setup/CourtTypeEditPage';
import CourtsListPage from './pages/Setup/CourtsListPage';
import CourtCreatePage from './pages/Setup/CourtCreatePage';
import CourtDetailPage from './pages/Setup/CourtDetailPage';
import CourtEditPage from './pages/Setup/CourtEditPage';
import DepartmentsListPage from './pages/Setup/DepartmentsListPage';
import DepartmentCreatePage from './pages/Setup/DepartmentCreatePage';
import DepartmentDetailPage from './pages/Setup/DepartmentDetailPage';
import DepartmentEditPage from './pages/Setup/DepartmentEditPage';
import JudgesListPage from './pages/Judges/JudgesListPage';
import JudgeCreatePage from './pages/Judges/JudgeCreatePage';
import JudgeDetailPage from './pages/Judges/JudgeDetailPage';
import JudgeEditPage from './pages/Judges/JudgeEditPage';
import UsersListPage from './pages/Settings/UsersListPage';
import RolesListPage from './pages/Settings/RolesListPage';
import CourtCasesListPage from './pages/CourtCases/CourtCasesListPage';
import CourtCaseCreatePage from './pages/CourtCases/CourtCaseCreatePage';
import CourtCaseDetailPage from './pages/CourtCases/CourtCaseDetailPage';
import CourtCaseEditPage from './pages/CourtCases/CourtCaseEditPage';
import FormsListPage from './pages/Forms/FormsListPage';
import FormCreatePage from './pages/Forms/FormCreatePage';
import FormDetailPage from './pages/Forms/FormDetailPage';
import FormEditPage from './pages/Forms/FormEditPage';
import AppointmentsListPage from './pages/Appointments/AppointmentsListPage';
import AppointmentCreatePage from './pages/Appointments/AppointmentCreatePage';
import AppointmentDetailPage from './pages/Appointments/AppointmentDetailPage';
import AppointmentEditPage from './pages/Appointments/AppointmentEditPage';
import ExpensesListPage from './pages/Expenses/ExpensesListPage';
import ExpenseCreatePage from './pages/Expenses/ExpenseCreatePage';
import ExpenseDetailPage from './pages/Expenses/ExpenseDetailPage';
import ExpenseEditPage from './pages/Expenses/ExpenseEditPage';
import InvoicesListPage from './pages/Invoices/InvoicesListPage';
import InvoiceCreatePage from './pages/Invoices/InvoiceCreatePage';
import InvoiceDetailPage from './pages/Invoices/InvoiceDetailPage';
import InvoiceEditPage from './pages/Invoices/InvoiceEditPage';
import LegalFormsBrowserPage from './pages/LegalForms/LegalFormsBrowserPage';
import LegalFormsPreviewPage from './pages/LegalForms/LegalFormsPreviewPage';
import SubscriptionRequiredPage from './pages/Subscription/SubscriptionRequiredPage';
import SubscriptionSuccessPage from './pages/Subscription/SubscriptionSuccessPage';

function RedirectCaseToCourtCase() {
  const { caseId } = useParams<{ caseId: string }>();
  return <Navigate to={`/dashboard/court-cases/${caseId ?? ''}`} replace />;
}
function RedirectCaseEditToCourtCaseEdit() {
  const { caseId } = useParams<{ caseId: string }>();
  return <Navigate to={`/dashboard/court-cases/${caseId ?? ''}/edit`} replace />;
}

function RedirectSetupId(base: 'countries' | 'states' | 'cities' | 'case-types' | 'court-types' | 'courts' | 'departments') {
  const C = () => {
    const { id } = useParams<{ id: string }>();
    return <Navigate to={`/dashboard/settings/${base}/${id ?? ''}`} replace />;
  };
  return C;
}
function RedirectSetupIdEdit(base: 'case-types' | 'court-types' | 'courts' | 'departments') {
  const C = () => {
    const { id } = useParams<{ id: string }>();
    return <Navigate to={`/dashboard/settings/${base}/${id ?? ''}/edit`} replace />;
  };
  return C;
}
const RedirectSetupCountriesId = RedirectSetupId('countries');
const RedirectSetupStatesId = RedirectSetupId('states');
const RedirectSetupCitiesId = RedirectSetupId('cities');
const RedirectSetupCaseTypesId = RedirectSetupId('case-types');
const RedirectSetupCaseTypesIdEdit = RedirectSetupIdEdit('case-types');
const RedirectSetupCourtTypesId = RedirectSetupId('court-types');
const RedirectSetupCourtTypesIdEdit = RedirectSetupIdEdit('court-types');
const RedirectSetupCourtsId = RedirectSetupId('courts');
const RedirectSetupCourtsIdEdit = RedirectSetupIdEdit('courts');
const RedirectSetupDepartmentsId = RedirectSetupId('departments');
const RedirectSetupDepartmentsIdEdit = RedirectSetupIdEdit('departments');

function RedirectJudgesId() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/dashboard/settings/judges/${id ?? ''}`} replace />;
}
function RedirectJudgesIdEdit() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/dashboard/settings/judges/${id ?? ''}/edit`} replace />;
}

function AppToaster() {
  const { theme } = useTheme();
  return <Toaster position="top-right" theme={theme} richColors closeButton />;
}

const App: React.FC = () => {
  useEffect(() => {
    initDb();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider>
          <AppToaster />
          <SidebarProvider>
            <BrowserRouter
              future={{ v7_relativeSplatPath: true, v7_startTransition: true }}
            >
              <Routes>
            {/* Public Website Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/trial-register" element={<TrialRegisterPage />} />
            <Route path="/tips" element={<TipsPage />} />
            {/* Add other public routes here when ready */}
            {/* <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} /> */}
            {/* <Route path="/services" element={<PublicLayout><ServicesPage /></PublicLayout>} /> */}
            {/* <Route path="/blog" element={<PublicLayout><BlogPage /></PublicLayout>} /> */}
            {/* <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} /> */}

            {/* Auth routes - outside MainLayout */}
            <Route path="/login" element={<SignInPage />} />
            <Route path="/sign-in" element={<SignInPage />} />
            <Route path="/sign-up" element={<SignUpPage />} />
            <Route path="/register" element={<SignUpPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />

            {/* Profile route - redirect to dashboard/profile */}
            <Route path="/profile" element={<Navigate to="/dashboard/profile" replace />} />

            {/* Hub — standalone, no sidebar */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <HubPage />
                </ProtectedRoute>
              }
            />

            {/* Subscription pages — standalone (no sidebar/header), must be before /dashboard/* */}
            <Route
              path="/dashboard/subscription/required"
              element={
                <ProtectedRoute>
                  <SubscriptionRequiredPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/subscription/success"
              element={
                <ProtectedRoute>
                  <SubscriptionSuccessPage />
                </ProtectedRoute>
              }
            />

            {/* Protected routes - inside MainLayout */}
            <Route
              path="/dashboard/*"
              element={
                <ProtectedRoute>
                  <MainLayout>
                  <Routes>
                    <Route path="/" element={<Navigate to="/dashboard/overview" replace />} />
                    <Route path="/overview" element={<DashboardPage />} />

                    <Route path="/calendar" element={<CalendarPage />} />
                    <Route path="/budget" element={<BudgetPage />} />
                    <Route path="/budget/new" element={<BudgetCreatePage />} />
                    <Route path="/budget/:id" element={<BudgetDetailPage />} />
                    <Route path="/billing" element={<BillingPage />} />
                    <Route path="/reports" element={<ReportsPage />} />
                    <Route path="/settings" element={<SettingsLayout />}>
                      <Route index element={<Navigate to="appearance" replace />} />
                      <Route path="appearance" element={<SettingsPage />} />
                      <Route path="general" element={<SettingsPage />} />
                      <Route path="smtp" element={<SettingsPage />} />
                      <Route path="analytics" element={<SettingsPage />} />
                      <Route path="notifications" element={<SettingsPage />} />
                      <Route path="users" element={<UsersListPage />} />
                      <Route path="roles" element={<RolesListPage />} />
                      <Route path="countries" element={<CountriesListPage />} />
                      <Route path="countries/:id" element={<CountryDetailPage />} />
                      <Route path="states" element={<StatesListPage />} />
                      <Route path="states/:id" element={<StateDetailPage />} />
                      <Route path="cities" element={<CitiesListPage />} />
                      <Route path="cities/:id" element={<CityDetailPage />} />
                      <Route path="case-types" element={<CaseTypesListPage />} />
                      <Route path="case-types/new" element={<CaseTypeCreatePage />} />
                      <Route path="case-types/:id" element={<CaseTypeDetailPage />} />
                      <Route path="case-types/:id/edit" element={<CaseTypeEditPage />} />
                      <Route path="court-types" element={<CourtTypesListPage />} />
                      <Route path="court-types/new" element={<CourtTypeCreatePage />} />
                      <Route path="court-types/:id" element={<CourtTypeDetailPage />} />
                      <Route path="court-types/:id/edit" element={<CourtTypeEditPage />} />
                      <Route path="courts" element={<CourtsListPage />} />
                      <Route path="courts/new" element={<CourtCreatePage />} />
                      <Route path="courts/:id" element={<CourtDetailPage />} />
                      <Route path="courts/:id/edit" element={<CourtEditPage />} />
                      <Route path="departments" element={<DepartmentsListPage />} />
                      <Route path="departments/new" element={<DepartmentCreatePage />} />
                      <Route path="departments/:id" element={<DepartmentDetailPage />} />
                      <Route path="departments/:id/edit" element={<DepartmentEditPage />} />
                      <Route path="judges" element={<JudgesListPage />} />
                      <Route path="judges/new" element={<JudgeCreatePage />} />
                      <Route path="judges/:id" element={<JudgeDetailPage />} />
                      <Route path="judges/:id/edit" element={<JudgeEditPage />} />
                    </Route>
                    <Route path="/profile" element={<ProfilePage />} />

                    <Route path="/cases" element={<Navigate to="/dashboard/court-cases" replace />} />
                    <Route path="/cases/new" element={<Navigate to="/dashboard/court-cases/new" replace />} />
                    <Route path="/cases/:caseId" element={<RedirectCaseToCourtCase />} />
                    <Route path="/cases/:caseId/edit" element={<RedirectCaseEditToCourtCaseEdit />} />

                    <Route path="/court-cases" element={<CourtCasesListPage />} />
                    <Route path="/court-cases/new" element={<CourtCaseCreatePage />} />
                    <Route path="/court-cases/:id" element={<CourtCaseDetailPage />} />
                    <Route path="/court-cases/:id/edit" element={<CourtCaseEditPage />} />

                    <Route path="/forms-and-precedents" element={<FormsListPage />} />
                    <Route path="/forms-and-precedents/new" element={<FormCreatePage />} />
                    <Route path="/forms-and-precedents/:id" element={<FormDetailPage />} />
                    <Route path="/forms-and-precedents/:id/edit" element={<FormEditPage />} />

                    <Route path="/legal-forms" element={<LegalFormsBrowserPage />} />
                    <Route path="/legal-forms/folder/:folderId" element={<LegalFormsBrowserPage />} />
                    <Route path="/legal-forms/file/:fileId" element={<LegalFormsPreviewPage />} />

                    <Route path="/appointments" element={<AppointmentsListPage />} />
                    <Route path="/appointments/new" element={<AppointmentCreatePage />} />
                    <Route path="/appointments/:id" element={<AppointmentDetailPage />} />
                    <Route path="/appointments/:id/edit" element={<AppointmentEditPage />} />

                    <Route path="/expenses" element={<ExpensesListPage />} />
                    <Route path="/expenses/new" element={<ExpenseCreatePage />} />
                    <Route path="/expenses/:id" element={<ExpenseDetailPage />} />
                    <Route path="/expenses/:id/edit" element={<ExpenseEditPage />} />

                    <Route path="/invoices" element={<InvoicesListPage />} />
                    <Route path="/invoices/new" element={<InvoiceCreatePage />} />
                    <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
                    <Route path="/invoices/:id/edit" element={<InvoiceEditPage />} />

                    <Route path="/clients" element={<ClientsListPage />} />
                    <Route path="/clients/new" element={<ClientCreatePage />} />
                    <Route path="/clients/:clientId" element={<ClientDetailPage />} />
                    <Route path="/clients/:clientId/edit" element={<ClientEditPage />} />

                    <Route path="/judges" element={<Navigate to="/dashboard/settings/judges" replace />} />
                    <Route path="/judges/new" element={<Navigate to="/dashboard/settings/judges/new" replace />} />
                    <Route path="/judges/:id" element={<RedirectJudgesId />} />
                    <Route path="/judges/:id/edit" element={<RedirectJudgesIdEdit />} />

                    <Route path="/documents" element={<DocumentsListPage />} />
                    <Route path="/documents/new" element={<DocumentCreatePage />} />
                    <Route path="/documents/:documentId" element={<DocumentDetailPage />} />
                    <Route path="/documents/:documentId/sign" element={<DocumentSigningPage />} />
                    <Route path="/documents/:documentId/edit" element={<DocumentEditPage />} />

                    <Route path="/workflows" element={<WorkflowsListPage />} />
                    <Route path="/workflows/new" element={<WorkflowCreatePage />} />
                    <Route path="/workflows/:id" element={<WorkflowDetailPage />} />
                    <Route path="/workflows/:id/edit" element={<WorkflowEditPage />} />
                    <Route path="/workflows/:id/diagram" element={<WorkflowDiagramPage />} />
                    <Route path="/workflows/:workflowId/tasks/new" element={<TaskCreateForWorkflowPage />} />
                    <Route path="/tasks" element={<TasksListPage />} />
                    <Route path="/tasks/new" element={<TaskCreatePage />} />
                    <Route path="/tasks/:taskId" element={<TaskDetailPage />} />
                    <Route path="/tasks/:taskId/edit" element={<TaskEditPage />} />

                    {/* Redirects: /dashboard/setup/* -> /dashboard/settings/* (backwards compatibility) */}
                    <Route path="/setup/countries" element={<Navigate to="/dashboard/settings/countries" replace />} />
                    <Route path="/setup/countries/:id" element={<RedirectSetupCountriesId />} />
                    <Route path="/setup/states" element={<Navigate to="/dashboard/settings/states" replace />} />
                    <Route path="/setup/states/:id" element={<RedirectSetupStatesId />} />
                    <Route path="/setup/cities" element={<Navigate to="/dashboard/settings/cities" replace />} />
                    <Route path="/setup/cities/:id" element={<RedirectSetupCitiesId />} />
                    <Route path="/setup/case-types/new" element={<Navigate to="/dashboard/settings/case-types/new" replace />} />
                    <Route path="/setup/case-types/:id/edit" element={<RedirectSetupCaseTypesIdEdit />} />
                    <Route path="/setup/case-types/:id" element={<RedirectSetupCaseTypesId />} />
                    <Route path="/setup/case-types" element={<Navigate to="/dashboard/settings/case-types" replace />} />
                    <Route path="/setup/court-types/new" element={<Navigate to="/dashboard/settings/court-types/new" replace />} />
                    <Route path="/setup/court-types/:id/edit" element={<RedirectSetupCourtTypesIdEdit />} />
                    <Route path="/setup/court-types/:id" element={<RedirectSetupCourtTypesId />} />
                    <Route path="/setup/court-types" element={<Navigate to="/dashboard/settings/court-types" replace />} />
                    <Route path="/setup/courts/new" element={<Navigate to="/dashboard/settings/courts/new" replace />} />
                    <Route path="/setup/courts/:id/edit" element={<RedirectSetupCourtsIdEdit />} />
                    <Route path="/setup/courts/:id" element={<RedirectSetupCourtsId />} />
                    <Route path="/setup/courts" element={<Navigate to="/dashboard/settings/courts" replace />} />
                    <Route path="/setup/departments/new" element={<Navigate to="/dashboard/settings/departments/new" replace />} />
                    <Route path="/setup/departments/:id/edit" element={<RedirectSetupDepartmentsIdEdit />} />
                    <Route path="/setup/departments/:id" element={<RedirectSetupDepartmentsId />} />
                    <Route path="/setup/departments" element={<Navigate to="/dashboard/settings/departments" replace />} />

                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </MainLayout>
                </ProtectedRoute>
              }
            />
              </Routes>
              <Chatbot />
            </BrowserRouter>
          </SidebarProvider>
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
